import { useEffect, useState } from 'react'
import { Clock, X } from 'lucide-react'
import { useCopilotStore, type CopilotCard } from '../../stores/copilotStore'

interface Props {
  callSid: string
  readOnly?: boolean
}

const KIND_STYLES: Record<string, string> = {
  question: 'border-blue-500/40 bg-blue-500/5',
  objection: 'border-orange-500/40 bg-orange-500/5',
  opportunity: 'border-green-500/40 bg-green-500/5',
  warning: 'border-red-500/40 bg-red-500/5',
}

// Stable empty reference — returning `?? []` from inside a Zustand selector
// produces a fresh array each call and loops useSyncExternalStore.
const EMPTY_CARDS: ReadonlyArray<CopilotCard> = []

export function CoachingCardStack({ callSid, readOnly = false }: Props) {
  const cards = useCopilotStore((s) => s.byCallSid[callSid]?.cards) ?? EMPTY_CARDS
  const dismissCard = useCopilotStore((s) => s.dismissCard)
  const snoozeCard = useCopilotStore((s) => s.snoozeCard)
  // Tick once per second so snoozed cards reappear when their timer expires.
  // Initialized to 0 so React doesn't see Date.now() being called in render.
  const [now, setNow] = useState(0)

  useEffect(() => {
    // Post-call review shows every card and never snoozes, so the ticker is
    // pure overhead there — skip it.
    if (readOnly) return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [readOnly])

  const visible = (
    readOnly
      ? cards
      : cards
          .filter((c) => !c.dismissed)
          .filter((c) => !c.snoozedUntil || c.snoozedUntil <= now)
  ).sort((a, b) => b.priority - a.priority || b.ts - a.ts)

  return (
    <section className="rounded-lg border border-border bg-surface-card p-4">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Coaching</h3>
        <span className="text-xs text-text-secondary">{visible.length} active</span>
      </header>
      {visible.length === 0 ? (
        <p className="text-xs text-text-secondary">No active suggestions.</p>
      ) : (
        <ul className="space-y-2">
          {visible.map((card) => (
            <li
              key={card.id}
              className={`rounded border px-3 py-2 ${KIND_STYLES[card.kind] ?? 'border-border-subtle bg-surface-elevated'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase text-text-secondary">
                    <span>{card.kind}</span>
                    <span aria-label="priority">P{card.priority}</span>
                  </div>
                  <div className="text-sm font-medium text-text-primary">{card.title}</div>
                  <div className="text-sm text-text-primary">{card.body}</div>
                </div>
                {!readOnly && (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => snoozeCard(callSid, card.id, 30_000)}
                      className="rounded p-1 text-text-secondary hover:bg-surface hover:text-text-primary"
                      aria-label="Snooze 30s"
                      title="Snooze 30s"
                    >
                      <Clock className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => dismissCard(callSid, card.id)}
                      className="rounded p-1 text-text-secondary hover:bg-surface hover:text-text-primary"
                      aria-label="Dismiss"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
